import { useState, useMemo } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { useModals } from '../context/ModalContext';
import MonthlyIncomeChart from '../components/charts/MonthlyIncomeChart';
import toast from 'react-hot-toast';

function DividendsPage() {
  const { dividends, isLoading, deleteDividend } = usePortfolio();
  const { openDividend } = useModals();
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const sortedDividends = useMemo(() => {
    return [...(dividends || [])].sort((a, b) => {
      const aVal = a[sortKey] || '';
      const bVal = b[sortKey] || '';
      if (sortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [dividends, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this dividend?')) return;
    const success = await deleteDividend(id);
    if (success) {
      toast.success('Dividend deleted');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Compact variant for the cramped 3-up summary cards (£20K, £1.5K, £950).
  const formatCurrencyCompact = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Calculate 12m dividends
  const last12m = useMemo(() => {
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return (dividends || []).filter(d => new Date(d.date) >= cutoff);
  }, [dividends]);

  const totalAll = (dividends || []).reduce((sum, d) => sum + (d.amount_gbp || 0), 0);
  const total12m = last12m.reduce((sum, d) => sum + (d.amount_gbp || 0), 0);
  const monthlyAvg = total12m / 12;

  if (isLoading && !dividends?.length) {
    return <div className="loading-screen"><div className="loading-spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dividends</h1>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid dashboard-grid-3 is-kpi" style={{ marginBottom: '1.5rem' }}>
        <div className="summary-card purple">
          <div className="summary-card-label">Total</div>
          <div className="summary-card-value" title={formatCurrency(totalAll)}>
            {formatCurrencyCompact(totalAll)}
          </div>
          <div className="summary-card-sub">Lifetime</div>
        </div>
        <div className="summary-card purple">
          <div className="summary-card-label">Last 12m</div>
          <div className="summary-card-value" title={formatCurrency(total12m)}>
            {formatCurrencyCompact(total12m)}
          </div>
          <div className="summary-card-sub">~{formatCurrencyCompact(monthlyAvg)}/mo</div>
        </div>
        <div className="summary-card gold">
          <div className="summary-card-label">Yield (12m)</div>
          <div className="summary-card-value">
            {total12m > 0 && totalAll > 0 ? Math.round((total12m / totalAll) * 100) + '%' : '-'}
          </div>
          <div className="summary-card-sub">12m / total</div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ marginBottom: '1.5rem' }}>
        <MonthlyIncomeChart />
      </div>

      {/* Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <span className="data-table-title">Dividend History</span>
          <span className="data-table-badge">{dividends?.length || 0} payments</span>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')}>Date {sortKey === 'date' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('ticker')}>Ticker {sortKey === 'ticker' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th className="num" onClick={() => handleSort('amount_gbp')}>Amount (GBP) {sortKey === 'amount_gbp' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th className="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDividends.map(dividend => (
                <tr key={dividend.id}>
                  <td>{dividend.date}</td>
                  <td>{dividend.ticker?.toUpperCase()}</td>
                  <td className="pos num">{formatCurrency(dividend.amount_gbp)}</td>
                  <td className="center">
                    <div className="table-actions">
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => openDividend(dividend)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(dividend.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DividendsPage;
